import React, { useCallback, useEffect, useState } from 'react';
import { GOLD } from '../../config/radioConstants';
import {
  fetchEllaRadioBans,
  getCachedEllaRadioBans,
  probeEllaRadioBansApi,
  unbanEllaRadioArtist,
  unbanEllaRadioTrack,
} from '../../services/ellaRadioBansService';
import EllaRadioBansError from './EllaRadioBansError';
import { classifyBansError } from '../../services/ellaRadioBansErrors';
import { useTranslation } from '../../i18n/useTranslation';

function SectionHeader({ children }) {
  return (
    <h2 style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: 'var(--text-tertiary)',
      marginBottom: 12, marginTop: 28,
    }}>
      {children}
    </h2>
  );
}

export default function EllaRadioBansSettings({ compact = false }) {
  const { t } = useTranslation();
  const [available, setAvailable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cache, setCache] = useState(() => getCachedEllaRadioBans());
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ok = await probeEllaRadioBansApi();
      setAvailable(ok);
      if (!ok) return;
      const fresh = await fetchEllaRadioBans();
      setCache(fresh);
    } catch (e) {
      setError(classifyBansError(e));
      setCache(getCachedEllaRadioBans());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (available === false) return null;

  const removeTrack = async (trackId, source) => {
    try {
      const next = await unbanEllaRadioTrack(trackId, source);
      setCache(next);
    } catch (e) {
      setError(classifyBansError(e));
    }
  };

  const removeArtist = async (artist) => {
    try {
      const next = await unbanEllaRadioArtist({ banId: artist.id });
      setCache(next);
    } catch (e) {
      setError(classifyBansError(e));
    }
  };

  const hasItems = cache.bannedArtists.length > 0 || cache.bannedTracks.length > 0;
  const headingStyle = { fontSize: 11, fontWeight: 700, color: GOLD, marginBottom: 8, letterSpacing: '0.06em' };
  const unbanLabel = t('ellaBans.unban', { defaultValue: t('ellaBans.remove') });

  return (
    <>
      {compact ? (
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>
          {t('ellaBans.sectionTitle')}
        </div>
      ) : (
        <SectionHeader>{t('ellaBans.sectionTitle')}</SectionHeader>
      )}
      <div style={{
        padding: compact ? '10px 12px' : '14px 16px',
        borderRadius: 12,
        background: compact ? 'rgba(255,255,255,0.04)' : 'var(--bg-secondary, rgba(255,255,255,0.04))',
        border: '1px solid var(--border, rgba(255,255,255,0.08))',
      }}>
        {loading && (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-tertiary)' }}>{t('ellaBans.loading')}</p>
        )}
        {!loading && error && <EllaRadioBansError kind={error} onRetry={load} />}
        {!loading && !hasItems && (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-tertiary)' }}>{t('ellaBans.empty')}</p>
        )}

        {cache.bannedArtists.length > 0 && (
          <div style={{ marginBottom: cache.bannedTracks.length ? 16 : 0 }}>
            <div style={headingStyle}>
              {t('ellaBans.artistsHeading')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {cache.bannedArtists.map((a) => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{a.displayName}</span>
                  <button
                    type="button"
                    onClick={() => removeArtist(a)}
                    style={{
                      padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                      color: 'var(--text-secondary)', cursor: 'pointer',
                    }}
                  >
                    {unbanLabel}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {cache.bannedTracks.length > 0 && (
          <div>
            <div style={headingStyle}>
              {t('ellaBans.tracksHeading')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {cache.bannedTracks.map((tr) => (
                <div key={`${tr.source}:${tr.trackId}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 13, color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                    {tr.trackId}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeTrack(tr.trackId, tr.source)}
                    style={{
                      padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)',
                      color: 'var(--text-secondary)', cursor: 'pointer',
                    }}
                  >
                    {unbanLabel}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
